import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
    AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, Film, Loader2, Eye, Upload, X as XIcon, Paperclip, FileText, Code2 } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";
import VideoStoryboardDialog from "@/components/curriculum/VideoStoryboardDialog";
import LessonVideoRenderPanel from "@/components/curriculum/LessonVideoRenderPanel";
import StudentPreviewDialog from "@/components/curriculum/StudentPreviewDialog";
import { API } from "@/lib/api";

const BLANK = {
    title: "",
    content_type: "text",
    content: "",
    video_url: "",
    order: 0,
    video_storyboard: null,
    attachments: [],
    minutes_allotted: null,
};

export default function LessonDialog({
    open,
    onOpenChange,
    chapterId,
    programId,
    chapterTitle,
    lesson,
    onSaved,
}) {
    const [form, setForm] = useState(BLANK);
    const [saving, setSaving] = useState(false);
    const [aiBusy, setAiBusy] = useState(null); // "content" | "video" | null
    const [storyboard, setStoryboard] = useState(null);
    const [confirmReplaceOpen, setConfirmReplaceOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [showRawHtml, setShowRawHtml] = useState(false);
    // After attaching a storyboard we briefly highlight the render panel
    // so users notice the next step. Cleared by a timer.
    const [highlightRender, setHighlightRender] = useState(false);
    // Tracks the saved lesson id after an initial save when we keep the
    // dialog open so the user can render the MP4. Null until first save.
    const [savedLessonId, setSavedLessonId] = useState(null);
    const renderPanelRef = useRef(null);
    const isEdit = !!lesson || !!savedLessonId;
    const effectiveLessonId = lesson?.id || savedLessonId;

    // Lesson types created by bulk-import or PDF-conversion contain
    // auto-generated HTML stubs that look terrifying in a raw textarea
    // (lots of inline styles, attachment cards, etc.). Hide that mess
    // behind a friendly summary card and only show the raw HTML when
    // the user explicitly asks for it.
    const isAutoStub =
        (lesson?.content_type === "pdf-attachment"
            || lesson?.content_type === "slideshow")
        && !!form.content
        && /<(div|a|p|img)[\s>]/i.test(form.content || "");

    useEffect(() => {
        if (open) {
            setForm(
                lesson
                    ? {
                          title: lesson.title || "",
                          content_type: lesson.content_type || "text",
                          content: lesson.content || "",
                          video_url: lesson.video_url || "",
                          order: lesson.order ?? 0,
                          video_storyboard: lesson.video_storyboard || null,
                          attachments: lesson.attachments || [],
                          minutes_allotted: lesson.minutes_allotted ?? null,
                      }
                    : BLANK,
            );
            setStoryboard(null);
            // Reset session-only state every time the dialog opens.
            setSavedLessonId(null);
            setHighlightRender(false);
        }
    }, [open, lesson]);

    // Pulse the render panel for a few seconds after the storyboard is
    // attached OR after an initial save with a pending storyboard. The
    // pulse + auto-scroll together answer the most common confusion:
    // "I clicked Convert to video, where is the video?"
    useEffect(() => {
        if (!highlightRender) return;
        // Scroll the render panel into view inside the dialog body.
        renderPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        const t = window.setTimeout(() => setHighlightRender(false), 4500);
        return () => window.clearTimeout(t);
    }, [highlightRender]);

    const save = async () => {
        if (!form.title.trim()) {
            toast.error("Title is required");
            return;
        }
        // When a freshly-attached storyboard exists but no MP4 has been
        // rendered, we keep the dialog open after save so the user can
        // immediately click "Render video" below. Without this, they'd
        // have to save → reopen the lesson → scroll to the bottom — the
        // exact "where is the video?" pain Bhaskar hit.
        const hasPendingStoryboard =
            !!form.video_storyboard && !form.video_url;
        setSaving(true);
        try {
            let result;
            if (isEdit) {
                const id = effectiveLessonId;
                const r = await api.patch(`/lessons/${id}`, {
                    ...form,
                    approved: true,
                });
                result = r.data;
                toast.success("Lesson updated");
            } else {
                const r = await api.post(`/chapters/${chapterId}/lessons`, form);
                result = r.data;
                toast.success("Lesson added");
            }
            if (hasPendingStoryboard) {
                // Stay on the dialog. Promote the just-created lesson id
                // so the render panel has a target to POST to, then
                // highlight + scroll to the "Render video" CTA.
                if (!isEdit && result?.id) setSavedLessonId(result.id);
                setHighlightRender(true);
                toast.message("Now click 'Render video' below to make the actual MP4.", {
                    duration: 8000,
                });
                onSaved?.();
                return;
            }
            onOpenChange(false);
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const generateContent = async () => {
        if (!form.title.trim()) {
            toast.error("Add a lesson title first — the AI writes against it.");
            return;
        }
        if (form.content && form.content.replace(/<[^>]*>/g, "").trim().length > 40) {
            setConfirmReplaceOpen(true);
            return;
        }
        await doGenerateContent();
    };

    const doGenerateContent = async () => {
        setAiBusy("content");
        try {
            const { data } = await api.post("/ai/lesson-content", {
                title: form.title.trim(),
                program_id: programId,
                chapter_id: chapterId,
            });
            setForm((f) => ({ ...f, content: data.content || "", content_type: "text" }));
            toast.success("Lesson content generated");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "AI generation failed");
        } finally {
            setAiBusy(null);
        }
    };

    const generateVideo = async () => {
        if (!form.title.trim()) {
            toast.error("Add a lesson title first.");
            return;
        }
        setAiBusy("video");
        try {
            const { data } = await api.post("/ai/lesson-video", {
                title: form.title.trim(),
                content: form.content || "",
                program_id: programId,
            });
            setStoryboard(data.storyboard);
            toast.success("Video storyboard ready — review below.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Video generation failed");
        } finally {
            setAiBusy(null);
        }
    };

    const acceptStoryboard = () => {
        setForm((f) => ({ ...f, video_storyboard: storyboard }));
        setStoryboard(null);
        // If this lesson is already in the database, we can render the MP4
        // right away — kick off the auto-scroll + highlight immediately.
        // Otherwise, walk the user through the save-first step.
        if (effectiveLessonId) {
            toast.success(
                "Script attached. Save the lesson, then click 'Render video' below to create the MP4.",
                { duration: 7000 },
            );
            setHighlightRender(true);
        } else {
            toast.success(
                "Script attached. Click 'Save lesson' next — we'll then walk you straight to the 'Render video' button.",
                { duration: 7000 },
            );
        }
    };

    const aiButtons = (
        <>
            <Button
                type="button"
                size="sm"
                onClick={generateContent}
                disabled={aiBusy !== null}
                className="ai-shimmer gap-1.5"
                data-testid="ai-generate-lesson-btn"
            >
                {aiBusy === "content" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                    <Sparkles className="h-3.5 w-3.5" />
                )}
                <span className="text-xs font-semibold">
                    {aiBusy === "content" ? "Writing…" : "AI: Generate lesson"}
                </span>
            </Button>
            <Button
                type="button"
                size="sm"
                onClick={generateVideo}
                disabled={aiBusy !== null}
                className="ai-shimmer gap-1.5"
                data-testid="ai-convert-to-video-btn"
            >
                {aiBusy === "video" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                    <Film className="h-3.5 w-3.5" />
                )}
                <span className="text-xs font-semibold">
                    {aiBusy === "video" ? "Scripting…" : "AI: Convert to video"}
                </span>
            </Button>
        </>
    );

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent
                    className="max-w-4xl max-h-[90vh] overflow-y-auto"
                    data-testid="lesson-dialog"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display">
                            {isEdit ? "Edit lesson" : "Add new lesson"}
                        </DialogTitle>
                        <DialogDescription className="sr-only">
                            Edit lesson title, content type, and rich text body. Use the AI
                            buttons to generate lesson content or a video storyboard.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Lesson title</Label>
                            <Input
                                value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                placeholder="e.g. Welcome to the course"
                                data-testid="lesson-title-input"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Content type</Label>
                                <Select
                                    value={form.content_type}
                                    onValueChange={(v) => setForm({ ...form, content_type: v })}
                                >
                                    <SelectTrigger data-testid="lesson-type-trigger">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="text">Text article</SelectItem>
                                        <SelectItem value="video">Video</SelectItem>
                                        <SelectItem value="presentation">Presentation (slides)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Order</Label>
                                <Input
                                    type="number"
                                    value={form.order}
                                    onChange={(e) =>
                                        setForm({ ...form, order: Number(e.target.value) })
                                    }
                                    data-testid="lesson-order-input"
                                />
                            </div>
                        </div>

                        <div>
                            <Label className="flex items-center gap-2">
                                Minutes allotted
                                <span className="text-[10px] uppercase tracking-wider text-primary/70 font-semibold">
                                    Required for diploma programs
                                </span>
                            </Label>
                            <Input
                                type="number"
                                min={1}
                                max={600}
                                value={form.minutes_allotted ?? ""}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        minutes_allotted:
                                            e.target.value.trim() === ""
                                                ? null
                                                : Math.max(1, Math.min(600, parseInt(e.target.value, 10) || 1)),
                                    })
                                }
                                placeholder="e.g. 45"
                                data-testid="lesson-minutes-allotted-input"
                                className="bg-background"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Estimated focused study time for this lesson. The 60% chapter
                                time-gate uses the <em>sum</em> of all lesson minutes within a
                                chapter to determine when the next chapter unlocks for diploma
                                students.
                            </p>
                        </div>

                        {form.content_type === "video" && (
                            <div>
                                <Label>Video URL (YouTube, Vimeo or direct link)</Label>
                                <Input
                                    value={form.video_url}
                                    onChange={(e) =>
                                        setForm({ ...form, video_url: e.target.value })
                                    }
                                    placeholder="https://www.youtube.com/embed/…"
                                    data-testid="lesson-video-url-input"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                    Use embeddable URLs (e.g. <code>youtube.com/embed/ID</code>) so
                                    the video plays inline.
                                </p>
                            </div>
                        )}

                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <Label>
                                    {form.content_type === "video"
                                        ? "Description / transcript"
                                        : "Lesson content"}
                                </Label>
                                {form.video_storyboard ? (
                                    <span
                                        className="text-xs text-primary font-medium flex items-center gap-1"
                                        data-testid="storyboard-attached-badge"
                                    >
                                        <Film className="h-3.5 w-3.5" />
                                        Video storyboard attached
                                    </span>
                                ) : null}
                            </div>

                            {/* "Where's the video?" hint — surfaces only when a
                                fresh storyboard exists but no MP4 yet, telling
                                the user exactly what to click next. */}
                            {form.video_storyboard && !form.video_url && (
                                <div
                                    className="mb-3 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-primary flex items-center gap-2"
                                    data-testid="storyboard-next-step-hint"
                                >
                                    <Film className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span>
                                        <strong>Next:</strong>{" "}
                                        {effectiveLessonId
                                            ? "scroll down to the “Render video” button to make the actual MP4."
                                            : "click “Save lesson” below — we'll then walk you to the render button."}
                                    </span>
                                </div>
                            )}

                            {form.content_type === "text" ? (
                                <RichTextEditor
                                    value={form.content}
                                    onChange={(html) => setForm((f) => ({ ...f, content: html }))}
                                    placeholder="Start writing — or click “AI: Generate lesson” above to draft it for you."
                                    rightSlot={aiButtons}
                                    minHeight={340}
                                />
                            ) : isAutoStub && !showRawHtml ? (
                                <div
                                    className="rounded-md border border-border bg-muted/20 p-4 space-y-3"
                                    data-testid="lesson-autostub-card"
                                >
                                    <div className="flex items-start gap-3">
                                        <FileText className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-semibold">
                                                {lesson?.content_type === "slideshow"
                                                    ? "Slideshow lesson"
                                                    : "PDF-attached lesson"}
                                            </div>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {lesson?.content_type === "slideshow"
                                                    ? "Students see a paginated slideshow of every page in the attached PDF, with snip-to-AI and read-aloud built in."
                                                    : "Students see an 'Open PDF' card linking to the attached file. To turn it into a slideshow or rich text, use the Convert PDF action in the curriculum builder."}
                                            </p>
                                            <div className="text-[11px] text-muted-foreground mt-2">
                                                The HTML below is auto-generated — you usually don't need to touch it.
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <div className="flex items-center gap-2">{aiButtons}</div>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setShowRawHtml(true)}
                                            className="gap-1.5 text-muted-foreground"
                                            data-testid="lesson-show-raw-html"
                                        >
                                            <Code2 className="h-3.5 w-3.5" />
                                            <span className="text-xs">Edit raw HTML</span>
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <div className="flex justify-end gap-2">
                                        {isAutoStub && showRawHtml ? (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setShowRawHtml(false)}
                                                className="gap-1.5 text-muted-foreground"
                                                data-testid="lesson-hide-raw-html"
                                            >
                                                <XIcon className="h-3.5 w-3.5" />
                                                <span className="text-xs">Hide HTML</span>
                                            </Button>
                                        ) : null}
                                        {aiButtons}
                                    </div>
                                    <Textarea
                                        rows={10}
                                        value={form.content}
                                        onChange={(e) =>
                                            setForm({ ...form, content: e.target.value })
                                        }
                                        placeholder="Short description or transcript for this lesson."
                                        className="text-sm font-mono"
                                        data-testid="lesson-content-input"
                                    />
                                </div>
                            )}

                            {form.video_storyboard ? (
                                <div
                                    className="mt-2 flex items-center justify-between rounded-md border border-dashed border-primary/30 bg-primary/5 px-3 py-2 text-xs"
                                    data-testid="storyboard-attached-row"
                                >
                                    <span className="text-primary">
                                        {(form.video_storyboard.slides || []).length} slide
                                        storyboard saved with this lesson.
                                    </span>
                                    <button
                                        type="button"
                                        className="text-destructive hover:underline"
                                        onClick={() =>
                                            setForm((f) => ({ ...f, video_storyboard: null }))
                                        }
                                        data-testid="storyboard-remove-btn"
                                    >
                                        Remove
                                    </button>
                                </div>
                            ) : null}

                            <div
                                ref={renderPanelRef}
                                className={`mt-3 rounded-lg transition-shadow ${
                                    highlightRender
                                        ? "ring-4 ring-primary/40 ring-offset-2 animate-pulse"
                                        : ""
                                }`}
                                data-testid="render-panel-wrap"
                            >
                                <LessonVideoRenderPanel
                                    lessonId={effectiveLessonId}
                                    lessonTitle={form.title}
                                    chapterId={chapterId}
                                    chapterTitle={chapterTitle}
                                    videoUrl={form.video_url}
                                    hasStoryboard={!!form.video_storyboard}
                                    onChanged={async (newVideoUrl) => {
                                        // Authoritative refresh: re-fetch
                                        // the lesson so the form picks
                                        // up not just `video_url` but
                                        // any other side-effects from
                                        // the render (storyboard
                                        // updates, attachments, etc.).
                                        // The render panel also caches
                                        // the new URL locally as an
                                        // immediate-feedback fallback,
                                        // so even if this network call
                                        // fails the <video> appears.
                                        if (effectiveLessonId) {
                                            try {
                                                const { data: fresh } = await api.get(
                                                    `/lessons/${effectiveLessonId}`,
                                                );
                                                setForm((f) => ({
                                                    ...f,
                                                    video_url: fresh.video_url || "",
                                                    video_storyboard:
                                                        fresh.video_storyboard ?? f.video_storyboard,
                                                }));
                                            } catch {
                                                // Best-effort fallback to the URL
                                                // the panel just emitted.
                                                if (newVideoUrl !== undefined) {
                                                    setForm((f) => ({
                                                        ...f,
                                                        video_url: newVideoUrl || "",
                                                    }));
                                                }
                                            }
                                        }
                                        onSaved?.();
                                    }}
                                    onBeforeRender={async () => {
                                        // Silently flush the latest form
                                        // state (including a freshly-
                                        // attached storyboard) to the
                                        // backend before the render API
                                        // is hit — fixes the "form says
                                        // attached but server says no"
                                        // race the user reported.
                                        if (!effectiveLessonId) {
                                            toast.error(
                                                "Save the lesson first, then click Render video.",
                                            );
                                            return false;
                                        }
                                        try {
                                            await api.patch(
                                                `/lessons/${effectiveLessonId}`,
                                                { ...form, approved: true },
                                            );
                                            return true;
                                        } catch (err) {
                                            toast.error(
                                                err?.response?.data?.detail
                                                || "Could not save the lesson before rendering.",
                                            );
                                            return false;
                                        }
                                    }}
                                />
                            </div>
                        </div>

                        <AttachmentsField
                            attachments={form.attachments || []}
                            onChange={(next) => setForm((f) => ({ ...f, attachments: next }))}
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setPreviewOpen(true)}
                            className="mr-auto gap-1.5"
                            data-testid="preview-as-student-btn"
                        >
                            <Eye className="h-4 w-4" />
                            Preview as student
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            data-testid="lesson-cancel-btn"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={save}
                            disabled={saving}
                            className="bg-primary hover:bg-primary/90"
                            data-testid="lesson-save-btn"
                        >
                            {saving ? "Saving…" : isEdit ? "Save changes" : "Add lesson"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <VideoStoryboardDialog
                open={!!storyboard}
                onOpenChange={(v) => !v && setStoryboard(null)}
                storyboard={storyboard}
                onUse={acceptStoryboard}
                onDiscard={() => setStoryboard(null)}
            />

            <StudentPreviewDialog
                open={previewOpen}
                onOpenChange={setPreviewOpen}
                title={form.title}
                chapterTitle={chapterTitle}
                content={form.content}
                storyboard={form.video_storyboard}
                contentType={form.content_type}
                videoUrl={form.video_url}
            />

            <AlertDialog open={confirmReplaceOpen} onOpenChange={setConfirmReplaceOpen}>
                <AlertDialogContent data-testid="confirm-ai-replace-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Replace existing content?</AlertDialogTitle>
                        <AlertDialogDescription>
                            The AI will regenerate the lesson body and replace what you&rsquo;ve
                            written so far. This cannot be undone from within the editor.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="confirm-ai-replace-cancel">
                            Keep my version
                        </AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-primary hover:bg-primary/90"
                            onClick={() => {
                                setConfirmReplaceOpen(false);
                                doGenerateContent();
                            }}
                            data-testid="confirm-ai-replace-confirm"
                        >
                            Replace with AI draft
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

function AttachmentsField({ attachments, onChange }) {
    const inputRef = React.useRef(null);
    const [busy, setBusy] = React.useState(false);

    const onUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        setBusy(true);
        try {
            for (const f of files) {
                if (f.size > 60 * 1024 * 1024) {
                    toast.error(`${f.name} is over 60 MB.`);
                    continue;
                }
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/uploads/lesson-attachment", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                if (data.converted_from_presentation) {
                    toast.success(
                        `Converted ${data.original_presentation_name || "presentation"} → PDF · works as a slideshow now`,
                    );
                }
                onChange([...(attachments || []), data]);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const remove = (idx) =>
        onChange((attachments || []).filter((_, i) => i !== idx));

    return (
        <div data-testid="lesson-attachments-field">
            <div className="flex items-center justify-between mb-2">
                <Label className="flex items-center gap-1.5">
                    <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
                    Attachments
                    <span className="text-xs text-muted-foreground font-normal">
                        — PDFs, PPTX, Word, images. PowerPoint decks auto-convert to PDF slideshow.
                    </span>
                </Label>
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => inputRef.current?.click()}
                    disabled={busy}
                    className="gap-1.5"
                    data-testid="lesson-attach-add-btn"
                >
                    {busy ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                        <Upload className="h-3.5 w-3.5" />
                    )}
                    {busy ? "Uploading…" : "Add file"}
                </Button>
                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={onUpload}
                    data-testid="lesson-attach-input"
                />
            </div>
            {attachments && attachments.length > 0 ? (
                <ul className="space-y-1.5">
                    {attachments.map((a, idx) => (
                        <li
                            key={idx}
                            className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/30 px-3 py-1.5"
                            data-testid={`lesson-attach-row-${idx}`}
                        >
                            <a
                                href={a.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-primary hover:underline truncate flex-1 min-w-0"
                            >
                                {a.name}
                            </a>
                            {a.converted_from_presentation && (
                                <span
                                    className="text-[10px] uppercase tracking-[0.14em] font-semibold bg-accent/15 text-accent px-1.5 py-0.5 rounded"
                                    title={`Converted from ${a.original_presentation_name || "presentation"}`}
                                >
                                    PPTX → PDF
                                </span>
                            )}
                            <span className="text-xs text-muted-foreground flex-shrink-0">
                                {a.size_bytes
                                    ? `${(a.size_bytes / 1024 / 1024).toFixed(1)} MB`
                                    : ""}
                            </span>
                            <button
                                type="button"
                                onClick={() => remove(idx)}
                                className="text-muted-foreground hover:text-destructive flex-shrink-0"
                                aria-label="Remove attachment"
                                data-testid={`lesson-attach-remove-${idx}`}
                            >
                                <XIcon className="h-3.5 w-3.5" />
                            </button>
                        </li>
                    ))}
                </ul>
            ) : (
                <div className="text-xs text-muted-foreground italic">
                    No files attached yet.
                </div>
            )}
        </div>
    );
}
